import React, { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faDownload, faCheck } from "@fortawesome/free-solid-svg-icons";

export default function StatsTab({
  isMobile,
  loading,
  aggData,
  slug,
  slugLoading,
  qrCodeUrl,
  qrCodeBase64,
}) {
  const [copySuccess, setCopySuccess] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const safeAgg = Array.isArray(aggData) ? aggData : [];
  const chartData = (() => {
    const byDay = new Map();
    safeAgg.forEach((d) => {
      const day = d.scan_day?.slice(0, 10);
      if (!day) return;
      if (!byDay.has(day)) byDay.set(day, { day, "Generic QR": 0, "Per-Property QR": 0 });
      const row = byDay.get(day);
      if (d.source === "qr_code_property") row["Per-Property QR"] += d.qr_scans || 0;
      else row["Generic QR"] += d.qr_scans || 0;
    });
    return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  })();

  const formatDay = (day) => {
    if (!day) return "";
    const d = new Date(day + "T00:00:00");
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: "12px 16px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
          border: "1px solid #e5e7eb",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>
          {formatDay(label)}
        </div>
        {payload.filter((p) => p.value > 0).map((p, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 2 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
            <span style={{ color: "#374151" }}>{p.name}:</span>
            <span style={{ fontWeight: 700, color: "#1f2937" }}>
              {new Intl.NumberFormat().format(p.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const card = {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    padding: isMobile ? "16px 12px" : "24px 28px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    marginBottom: 24,
    width: "100%",
    maxWidth: 900,
    marginLeft: "auto",
    marginRight: "auto",
  };

  const onboardingSteps = [
    {
      num: 1,
      title: "Copy your unique QR code link",
      main: slugLoading ? (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Spinner animation="border" size="sm" /> <span>Loading your QR code...</span>
        </span>
      ) : slug ? (
        <div style={{ color: "#374151", fontSize: 15 }}>
          Your link includes <b>campaign=qr_code</b> as a parameter so scans are
          attributed correctly. Use this link with any third-party QR generator,
          or use our ready-made flyer in the Generic QR tab.
        </div>
      ) : (
        <span>Unable to load your QR code link.</span>
      ),
      button: slug && (
        <button
          onClick={() => {
            navigator.clipboard.writeText(qrCodeUrl);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
          }}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "12px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            justifyContent: "center",
          }}
        >
          <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} size="lg" />
          Copy Link
        </button>
      ),
    },
    {
      num: 2,
      title: "Download your QR code image",
      main: (
        <>
          <div style={{ color: "#374151", fontSize: 15, marginBottom: 10 }}>
            Download the raw QR image and drop it into your own flyer, letter,
            or any other print design.
          </div>
          {qrCodeBase64 && (
            <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <img
                src={`data:image/png;base64,${qrCodeBase64}`}
                alt="QR code"
                style={{ width: 120, height: 120, margin: "0 auto 12px auto", border: "1px solid #eee", borderRadius: 8, display: "block" }}
              />
            </div>
          )}
        </>
      ),
      button: qrCodeBase64 && (
        <button
          onClick={() => {
            setDownloading(true);
            const link = document.createElement("a");
            link.href = `data:image/png;base64,${qrCodeBase64}`;
            link.download = "qr_code.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => setDownloading(false), 1000);
          }}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "12px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            justifyContent: "center",
          }}
        >
          {downloading ? (
            <Spinner animation="border" size="sm" style={{ color: "#fff" }} />
          ) : (
            <FontAwesomeIcon icon={faDownload} size="lg" />
          )}
          Download QR Code
        </button>
      ),
    },
    {
      num: 3,
      title: "Print and distribute",
      main: (
        <div style={{ color: "#374151", fontSize: 15 }}>
          Print your direct mail with the QR code and distribute it to your
          target area. Use a local print shop or any online print service.
        </div>
      ),
      button: null,
    },
    {
      num: 4,
      title: "Track results",
      main: (
        <div style={{ color: "#374151", fontSize: 15 }}>
          Once people start scanning, the results will appear in the chart
          above so you can track your campaign performance.
        </div>
      ),
      button: null,
    },
  ];

  if (loading) {
    return (
      <>
        <div style={card}>
          <Skeleton width={160} height={20} style={{ marginBottom: 16 }} />
          <Skeleton height={300} borderRadius={8} />
        </div>
        <div style={card}>
          <Skeleton width={160} height={18} style={{ marginBottom: 16 }} />
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                <Skeleton circle width={32} height={32} style={{ marginBottom: 8 }} />
                <Skeleton width="60%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="90%" height={12} />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={card}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", marginBottom: 20, marginTop: 0 }}>
          QR Code Scans
        </h3>
        {safeAgg.length > 0 ? (
          <div style={{ width: "100%", height: isMobile ? 240 : 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatDay}
                  fontSize={11}
                  tick={{ fill: "#9ca3af" }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickLine={false}
                />
                <YAxis
                  fontSize={11}
                  tick={{ fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37,99,235,0.06)" }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#6b7280" }} />
                <Bar dataKey="Generic QR" name="Generic QR" fill="#2563eb" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Per-Property QR" name="Per-Property QR" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "#6b7280",
              fontSize: 14,
              background: "#f9fafb",
              borderRadius: 8,
            }}
          >
            No scans yet. Once people start scanning your QR codes, daily
            counts will appear here.
          </div>
        )}
      </div>

      <div style={card}>
        <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 4, textAlign: "center" }}>
          Add the QR code to your own assets
        </h3>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 24, textAlign: "center" }}>
          Prefer to design your own flyer? Follow these steps to embed your
          QR code in any print material.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "2rem",
            justifyContent: "center",
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {onboardingSteps.map((step) => (
            <div
              key={step.num}
              style={{
                background: "#f8fafc",
                borderRadius: 16,
                padding: isMobile ? "1.25rem 1rem" : "1.75rem 1.5rem",
                boxShadow: "0 4px 16px rgba(0,0,0,0.07)",
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                height: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 40,
                  marginRight: 18,
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: 700,
                    color: "#10b981",
                    minWidth: 32,
                    textAlign: "center",
                    marginTop: 2,
                  }}
                >
                  {step.num}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  height: "100%",
                  position: "relative",
                }}
              >
                <h4
                  style={{
                    color: "#1f2937",
                    margin: "0 0 0.5rem 0",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  {step.title}
                </h4>
                <div style={{ width: "100%", textAlign: "left", flex: "0 0 auto" }}>
                  {step.main}
                </div>
                <div style={{ flex: 1 }} />
                {step.button && (
                  <div
                    style={{
                      marginTop: "auto",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    {step.button}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
